import {
  IUser,
  MockHook,
  useAsyncEffect,
  useSnackbarNotification,
  useUser,
} from '@jupiterone/jupiter-web-toolkit';
import { ChangeEvent, useState } from 'react';

/*
  Has MFA device
    - show enable / disable
      * dont change step in this case
    - show delete MFA device
      * change step to Not has MFA device
  Not has MFA device
    - show add MFA device button
      * change to Generate QR code step
  Generate QR code
    - show button to create QR code
      once pressed show the QR code to scan
      and show "next" button. On next press
      change to Verify MFA code step
  Verify MFA code step
    - show text box to enter MFA code
    - show verify button
    - once successfully verified go to Has MFA device step
*/

export enum Step {
  NO_DEVICE,
  GENERATE_QR_CODE,
  VERIFY_MFA_CODE,
  DELETE_MFA_DEVICE,
}

const useMfaSettingsLogic = () => {
  const notification = useSnackbarNotification();
  const {
    user,
    loading: userLoading,
    getMfaSecret,
    updateMfaPreferences,
    verifyMfaCode,
  } = useUser();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<Step>(Step.NO_DEVICE);
  const [mfaCode, setMfaCode] = useState<string>('');
  const [qrCodeUri, setQrCodeUri] = useState<string>('');

  useAsyncEffect(async () => {
    if (user) {
      setCurrentStep(resolveUserToStep(user));
      setLoading(false);
    }
  }, [userLoading]);

  const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMfaCode(e.target.value);
  };

  const handleGenerateQRCode = async () => {
    if (!user) throw new Error('User not found');
    setLoading(true);
    try {
      const { code } = await getMfaSecret();
      const qrCodeUri = encodeSecretUri({ code, email: user.profile.email });
      setQrCodeUri(qrCodeUri);
      setCurrentStep(Step.GENERATE_QR_CODE);
    } catch (error) {
      notification.error({
        message: 'Failed to fetch mfa secret',
        error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfaCode = async () => {
    try {
      const { status } = await verifyMfaCode(mfaCode);
      if (!status) {
        notification.warning('MFA code verification failed');
      } else {
        notification.info('MFA code verified');
        const { status } = await updateMfaPreferences({
          SoftwareTokenMfaSettings: {
            Enabled: true,
            PreferredMfa: true,
          },
        });
        if (!status) {
          notification.warning('Enabling MFA device failed');
        } else {
          notification.success('MFA device successfully enabled');
        }
        setCurrentStep(Step.DELETE_MFA_DEVICE);
      }
    } catch (error) {
      notification.error({
        message: 'Error occurred while verifying MFA code',
        error,
      });
    }
  };

  const handleDeleteMFADevice = async () => {
    setLoading(true);
    try {
      const { status } = await updateMfaPreferences({
        SoftwareTokenMfaSettings: {
          Enabled: false,
          PreferredMfa: false,
        },
      });
      if (!status) {
        notification.warning('Failed to delete MFA device');
      } else {
        notification.info('MFA device successfully deleted');
        setCurrentStep(Step.NO_DEVICE);
      }
    } catch (error) {
      notification.error({
        message: 'Error occurred while updating MFA preferences',
        error,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading: loading && userLoading,
    currentStep,
    mfaCode,
    qrCodeUri,
    handleDeleteMFADevice,
    handleCodeChange,
    handleVerifyMfaCode,
    handleGenerateQRCode,
  };
};

const resolveUserToStep = (user: IUser): Step => {
  let step: Step = Step.NO_DEVICE;
  const { mfaSettings } = user;
  const { settingList } = mfaSettings;
  if (settingList.length) {
    step = Step.DELETE_MFA_DEVICE;
  }
  return step;
};

interface EncodeSecretInput {
  email: string;
  code: string;
}
const encodeSecretUri = ({ email, code }: EncodeSecretInput) => {
  return `otpauth://totp/JupiterOne:${email}?secret=${code}&issuer=JupiterOne`;
};

export type MfaSettingsComponentProps = ReturnType<typeof useMfaSettingsLogic>;
export const MOCK_MFA_SETTINGS_PROPS: MfaSettingsComponentProps = {
  loading: false,
  currentStep: Step.NO_DEVICE,
  mfaCode: '',
  qrCodeUri: '',
  handleDeleteMFADevice: MockHook.mockFunction,
  handleCodeChange: MockHook.mockFunction,
  handleVerifyMfaCode: MockHook.mockFunction,
  handleGenerateQRCode: MockHook.mockFunction,
};
export default MockHook.useMockHook(
  useMfaSettingsLogic,
  MOCK_MFA_SETTINGS_PROPS
);
